import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import TagControl from './TagControl';

export const PdfRebrandingForm = () => {
    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => { document.title = 'OptiConfig PDF Rebranding'; }, []);

    const [token, setToken] = useState("");
    // Presets & replacements state
    const [allPresets, setAllPresets] = useState([]); // { name, replacements, discountPrc, headerImage, footerImage }
    const [preset, setPreset] = useState(null);
    const [customPresetName, setCustomPresetName] = useState('');

    const [replacements, setReplacements] = useState([]);
    const [discountPrc, setDiscountPrc] = useState(0); // 0-100
    const [headerImage, setHeaderImage] = useState(null);
    const [footerImage, setFooterImage] = useState(null);
    const [imageOptions, setImageOptions] = useState([{ label: '(none)', value: null }]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [sessionId, setSessionId] = useState(crypto.randomUUID());
    const [succeeded, setSucceeded] = useState(false);

    const toast = useRef(null);

    const showError = (err) => {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: err, life: 3000 });
    };
    const showSuccess = (msg) => {
        toast.current?.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
    };

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await fetchAccessToken(getAccessTokenSilently);
                setToken(accessToken);
            } catch (error) {
                console.error('Error fetching access token:', error);
                showError('Failed to fetch access token. Please try again.');
            }
        };
        fetchToken();
    }, [getAccessTokenSilently]);

    // // Load available header/footer images (optional)
    // useEffect(() => {
    //     const loadImages = async () => {
    //         if (!token) return;
    //         try {
    //             const resp = await fetch(`${process.env.REACT_APP_API_URL || ''}/pdf/images`, {
    //                 headers: { Authorization: `Bearer ${token}` }
    //             });
    //             if (!resp.ok) return; // silent fail keeps default
    //             const arr = await resp.json(); // expect array of strings
    //             if (Array.isArray(arr) && arr.length > 0) {
    //                 setImageOptions([{ label: '(none)', value: null }].concat(arr.map(n => ({ label: n, value: n }))));
    //             }
    //         } catch (_) { /* ignore */ }
    //     };
    //     loadImages();
    // }, [token]);

    // Load presets (specific to rebranding)
    useEffect(() => {
        const loadPresets = async () => {
            if (!token) return;
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL || ''}/pdf/presets_rb`, {
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
                });
                if (!resp.ok) return; // ignore errors silently
                const data = await resp.json();
                if (Array.isArray(data)) {
                    setAllPresets(data);
                }
            } catch (_) { /* ignore */ }
        };
        loadPresets();
    }, [token]);

    // Apply preset when selected
    useEffect(() => {
        const found = allPresets.find(p => p.name === preset);
        if (found) {
            setReplacements(found.replacements || []);
            setDiscountPrc(found.discountPrc ?? 0);
            setHeaderImage(found.headerImage ?? null);
            setFooterImage(found.footerImage ?? null);
            setCustomPresetName(found.name);
        }
    }, [preset, allPresets]);

    const presetOptions = allPresets.map(p => ({ label: p.name, value: p.name }));

    const saveCustomPreset = async () => {
        if (!customPresetName.trim()) return;
        const newPreset = {
            name: customPresetName.trim(),
            replacements,
            discountPrc: Math.min(100, Math.max(0, discountPrc || 0)),
            headerImage: headerImage || null,
            footerImage: footerImage || null
        };
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL || ''}/pdf/presets_rb`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(newPreset)
            });
            if (!resp.ok) await apiCallErrorHandler(resp, 'Failed to save preset');
            setAllPresets(prev => {
                const idx = prev.findIndex(p => p.name === newPreset.name);
                if (idx !== -1) {
                    const copy = [...prev];
                    copy[idx] = newPreset;
                    return copy;
                }
                return [...prev, newPreset];
            });
            setPreset(newPreset.name);
            showSuccess('Preset saved');
        } catch (err) {
            showError(err.message || 'Failed to save preset');
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!uploadedFiles || uploadedFiles.length === 0) {
            showError('Please upload at least one PDF file.');
            return;
        }
        setSubmitting(true);
        const fileNames = uploadedFiles.map(f => f.name);
        const boundedDiscount = Math.min(100, Math.max(0, discountPrc || 0));
        const payload = { 
            fileNames,
            replacements,
            discountPrc: boundedDiscount,
            headerImage: headerImage || undefined,
            footerImage: footerImage || undefined
        };
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/pdf/rebrand/${sessionId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }
            );
            if (!response.ok) await apiCallErrorHandler(response, 'Failed to submit. Please try again.');
            setSucceeded(true);
            showSuccess('Files processed successfully');
        } catch (error) {
            showError(error.message || 'Submission failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const discardSession = async (e) => {
        e?.preventDefault();
        try {
            await fetch(`${process.env.REACT_APP_API_URL || ''}/file/discard/${sessionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
        } catch (err) {
            showError(err.message || 'Failed to discard session on server.');
        } finally {
            setUploadedFiles([]);
            setSucceeded(false);
            setSessionId(crypto.randomUUID());
        }
    };

    const onDownload = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/file/download/${sessionId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (!response.ok) await apiCallErrorHandler(response, 'Failed to download. Please try again.');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${sessionId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showSuccess('File downloaded successfully');
            await discardSession();
        } catch (error) {
            showError(error.message || 'Download failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
            <Toast ref={toast} />
            <div className="col-12">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12">
                        <h4 style={{ textAlign: 'center' }}>PDF Rebranding Tool</h4>
                    </div>
                    <div className='col-3'></div>
                    <div className="field col-6" style={{ display: 'flex', justifyContent: 'center' }}>
                        {!succeeded && <Button
                            type="submit"
                            label={submitting ? "Submitting..." : "Submit"}
                            className="p-button-primary"
                            style={{ width: '50%' }}
                            disabled={uploadedFiles.length === 0 || submitting}
                        />}
                        {succeeded && <>
                            <Button
                                onClick={onDownload}
                                label={submitting ? "Downloading..." : "Download .zip"}
                                className="p-button-primary"
                                style={{ width: '50%' }}
                                disabled={uploadedFiles.length === 0 || submitting}
                            />
                        </>}
                        {uploadedFiles.length > 0 && (
                            <Button
                                onClick={discardSession}
                                label={"Discard"}
                                className="p-button-secondary"
                                style={{ width: '50%', marginLeft: 20 }}
                                disabled={submitting}
                            />
                        )}
                    </div>
                    <div className='col-3'></div>
                    <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                        <CustomFileUpload
                            acceptedFileTypes={['application/pdf', '.pdf']}
                            onMessage={(msg) => { showError(msg.detail); }}
                            token={token}
                            fileValue={uploadedFiles}
                            sessionId={sessionId}
                            onChangeFiles={(files) => {
                                setSucceeded(false);
                                setUploadedFiles(files);
                            }}
                        />
                    </div>
                    <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                        {/* Preset selection */}
                        <div className="p-field" style={{ marginTop: '1rem' }}>
                            <label htmlFor="presetDropdown">Presets</label>
                            <Dropdown
                                id="presetDropdown"
                                value={preset}
                                options={presetOptions}
                                onChange={(e) => setPreset(e.value)}
                                placeholder="Select a preset"
                                className="w-full"
                                showClear
                            />
                        </div>
                        {/* Replacements */}
                        <div className="p-field" style={{ marginTop: '1rem' }}>
                            <label htmlFor="listOfReplacements">List of Replacements</label>
                            <TagControl
                                placeholder="Specify text to replace/remove"
                                tags={replacements}
                                onChange={(tags) => {
                                    setReplacements(tags);
                                }} />
                        </div>
                        {/* Discount, Header, Footer */}
                        <div className="p-field" style={{ marginTop: '0.5rem' }}>
                            <label htmlFor="discountPrc">Discount</label>
                            <InputNumber
                                id="discountPrc"
                                value={discountPrc}
                                onValueChange={(e) => setDiscountPrc(e.value ?? 0)}
                                min={0}
                                max={100}
                                showButtons
                                useGrouping={false}
                                suffix="%"
                                style={{ width: '180px', marginLeft: '0.5rem' }}
                            />
                        </div>
                        <div className="p-field" style={{ marginTop: '0.5rem' }}>
                            <label htmlFor="headerImage">Header Image</label>
                            <Dropdown
                                id="headerImage"
                                value={headerImage}
                                options={imageOptions}
                                onChange={e => setHeaderImage(e.value)}
                                placeholder="Select header image"
                                className="w-full"
                                showClear
                                disabled
                            />
                        </div>
                        <div className="p-field" style={{ marginTop: '0.5rem' }}>
                            <label htmlFor="footerImage">Footer Image</label>
                            <Dropdown
                                id="footerImage"
                                value={footerImage}
                                options={imageOptions}
                                onChange={e => setFooterImage(e.value)}
                                placeholder="Select footer image"
                                className="w-full"
                                showClear
                                disabled
                            />
                        </div>
                        {/* Save preset */}
                        <div className="grid" style={{ marginTop: '1.25rem', alignItems: 'flex-end' }}>
                            <div className="col-6" style={{ paddingLeft: 0 }}>
                                <InputText
                                    type="text"
                                    value={customPresetName}
                                    onChange={e => setCustomPresetName(e.target.value)}
                                    placeholder="Preset name"
                                    className="w-full"
                                />
                            </div>
                            <div className="col-6" style={{ paddingLeft: 0 }}>
                                <Button
                                    type="button"
                                    label="Save as Preset"
                                    className="p-button-secondary w-full"
                                    onClick={saveCustomPreset}
                                    disabled={!customPresetName.trim()}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};